import React, { memo, useState } from 'react';
import { Box, Button, Chip, CircularProgress, Stack, Typography } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import DeleteConfirmDialog from '../../../shared/components/DeleteConfirmDialog';
import InlineAlert from '../../../shared/components/InlineAlert';
import WorkflowStages from '../../workflow/components/WorkflowStages';
import WorkflowPublishButton from './WorkflowPublishButton';
import type { Workflow } from '../../../types/backend';

interface Props {
  workflow: Workflow | null;
  loading: boolean;
  /** Every version of this workflow, oldest first, the draft last. */
  versions: string[];
  publishing: boolean;
  deleting: boolean;
  onSelectVersion: (version: string) => void;
  onEditDraft: () => void;
  onPublish: () => Promise<boolean>;
  onDelete: () => Promise<boolean>;
}

const versionLabel = (version: string) => (version === 'DRAFT' ? 'Draft' : `v${version}`);

// One version, read-only: the procedure an operator will be held to, its barriers, and which
// steps stop for a human. Editing always happens on the draft, so a published version offers to
// open that instead of pretending it can change.
const WorkflowViewer = memo<Props>(({
  workflow,
  loading,
  versions,
  publishing,
  deleting,
  onSelectVersion,
  onEditDraft,
  onPublish,
  onDelete,
}) => {
  const [confirmDelete, setConfirmDelete] = useState(false);

  if (loading) {
    return (
      <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!workflow) {
    return (
      <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          Select a workflow to see how it runs
        </Typography>
      </Box>
    );
  }

  const isDraft = workflow.version === 'DRAFT';

  return (
    <Box sx={{ flex: 1, overflowY: 'auto', p: 3 }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="h6">{workflow.title}</Typography>
        <Chip
          size="small"
          label={versionLabel(workflow.version)}
          color={isDraft ? 'default' : 'success'}
        />
        <Box sx={{ flex: 1 }} />
        <Button size="small" variant="outlined" startIcon={<EditIcon />} onClick={onEditDraft}>
          Edit draft
        </Button>
        {isDraft && <WorkflowPublishButton publishing={publishing} onPublish={onPublish} />}
        <Button
          size="small"
          color="error"
          startIcon={<DeleteIcon />}
          onClick={() => setConfirmDelete(true)}
          disabled={deleting}
        >
          {deleting ? 'Deleting...' : 'Delete'}
        </Button>
      </Stack>
      {versions.length > 1 && (
        <Stack direction="row" useFlexGap flexWrap="wrap" spacing={0.5} sx={{ mb: 1 }} aria-label="Versions">
          {versions.map((v) => (
            <Chip
              key={v}
              size="small"
              label={versionLabel(v)}
              variant={v === workflow.version ? 'filled' : 'outlined'}
              color={v === workflow.version ? 'primary' : 'default'}
              onClick={v === workflow.version ? undefined : () => onSelectVersion(v)}
            />
          ))}
        </Stack>
      )}
      {!isDraft && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          Published versions are immutable. Edit draft opens the working draft, which may already
          differ from this version.
        </Typography>
      )}
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {workflow.description || 'No description'}
      </Typography>

      <WorkflowStages nodes={workflow.nodes} />

      <DeleteConfirmDialog
        open={confirmDelete}
        title="Delete workflow?"
        onCancel={() => setConfirmDelete(false)}
        onConfirm={() => {
          setConfirmDelete(false);
          void onDelete();
        }}
        confirmText="Delete every version"
      >
        <Typography variant="body1" sx={{ mb: 2 }}>
          Delete <strong>{workflow.title}</strong> ({workflow.name})?
        </Typography>
        <InlineAlert severity="warning">
          Every version goes: {versions.map(versionLabel).join(', ') || versionLabel(workflow.version)}.
          This cannot be undone, and the workflow can no longer be started.
        </InlineAlert>
      </DeleteConfirmDialog>
    </Box>
  );
});

WorkflowViewer.displayName = 'WorkflowViewer';

export default WorkflowViewer;
